// src/platform — the ONE module allowed to know which platform we're on.
//
// Components never call Capacitor.* directly; they import capabilities and
// helpers from here. That keeps web/iOS divergence auditable in one place:
// `grep -rl "platform'" src/` is the parity audit.
//
// Native plugins (@capacitor/filesystem, share, status-bar, app) are
// dynamically imported so none of them land in the initial web bundle, and
// every call is wrapped so a plugin missing from an older installed binary
// degrades to a no-op instead of crashing OTA-updated JS (roadmap A3).
import { Capacitor } from '@capacitor/core';

export const isNative = Capacitor.isNativePlatform();

export const capabilities = {
  // Third-party login is offered on every platform. App Review Guideline 4.8
  // requires that an app offering Google/GitHub login ALSO offer Sign in with
  // Apple — so the Apple button is not optional here, it ships with the others.
  oauthButtons: true,
  // A4: R3F/three.js scenes never mount in WKWebView (memory ceilings).
  threeD: !isNative,
  // A11: WKWebView has no browser-download UX; native uses the share sheet.
  fileDownload: !isNative,
  hover: !isNative,
};

function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    // result is "data:<mime>;base64,<data>" — Filesystem wants bare base64
    reader.onload = () => resolve(String(reader.result).split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

// Exports/downloads: web anchors a download; native writes to the app cache
// and opens the iOS share sheet. All export buttons route through this.
export async function saveOrShare(blob, filename) {
  if (!isNative) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    return;
  }
  try {
    const [{ Filesystem, Directory }, { Share }] = await Promise.all([
      import('@capacitor/filesystem'),
      import('@capacitor/share'),
    ]);
    const data = await blobToBase64(blob);
    const written = await Filesystem.writeFile({
      path: filename,
      data,
      directory: Directory.Cache,
    });
    await Share.share({ title: filename, url: written.uri });
  } catch (e) {
    console.error('[platform] saveOrShare failed:', e);
    throw e;
  }
}

// Fires callback whenever the native app returns to the foreground.
// Returns an unsubscribe function; a no-op on web (the OS never suspends us).
export function onAppResume(callback) {
  if (!isNative) return () => {};
  let handle = null;
  let cancelled = false;
  import('@capacitor/app')
    .then(({ App }) =>
      App.addListener('appStateChange', ({ isActive }) => {
        if (isActive) callback();
      })
    )
    .then((h) => {
      if (cancelled) h.remove();
      else handle = h;
    })
    .catch(() => {});
  return () => {
    cancelled = true;
    handle?.remove();
  };
}

// Match the iOS status bar text to the app theme ('light' | 'dark' app theme;
// a dark app background needs light status-bar text = Style.Dark).
export async function syncStatusBar(theme) {
  if (!isNative) return;
  try {
    const { StatusBar, Style } = await import('@capacitor/status-bar');
    await StatusBar.setStyle({ style: theme === 'dark' ? Style.Dark : Style.Light });
  } catch (e) {
    // Plugin absent in this binary (A3 version skew) — no-op.
  }
}

// Push notifications (native only). Registration is user-initiated from the
// Settings toggle — the permission prompt is never shown at launch. Returns
// the APNs device token string, or null when not native, permission denied,
// or the plugin is absent from this binary (A3 version skew).
export async function enablePushNotifications() {
  if (!isNative) return null;
  try {
    const { PushNotifications } = await import('@capacitor/push-notifications');
    const perm = await PushNotifications.requestPermissions();
    if (perm.receive !== 'granted') return null;
    return await new Promise((resolve, reject) => {
      const timer = setTimeout(() => reject(new Error('Push registration timed out')), 15000);
      PushNotifications.addListener('registration', (t) => {
        clearTimeout(timer);
        resolve(t.value);
      });
      PushNotifications.addListener('registrationError', (e) => {
        clearTimeout(timer);
        reject(new Error(e?.error || 'Push registration error'));
      });
      PushNotifications.register();
    });
  } catch (e) {
    console.error('[platform] push registration failed:', e);
    return null;
  }
}

export async function disablePushNotifications() {
  if (!isNative) return;
  try {
    const { PushNotifications } = await import('@capacitor/push-notifications');
    await PushNotifications.unregister();
    await PushNotifications.removeAllListeners();
  } catch (e) {
    // Plugin absent (A3) — nothing to disable.
  }
}

// Wipe every trace of a stored auth session, on whichever platform we're on.
// Web keeps sessions in localStorage; native keeps them in Capacitor
// Preferences (see preferencesStorage.js). The 401-recovery path must clear
// BOTH — clearing only localStorage on native leaves the dead token in place
// and the app retries with it forever, which is exactly how a expired
// session turned into a permanent "Error Loading Application" dead end.
export async function clearAuthStorage() {
  try {
    Object.keys(window.localStorage).forEach((key) => {
      if (key.startsWith('sb-') || key === 'auth' || key === 'access_token') {
        window.localStorage.removeItem(key);
      }
    });
  } catch (e) {
    // Storage unavailable — nothing to clear.
  }
  if (!isNative) return;
  try {
    const { Preferences } = await import('@capacitor/preferences');
    await Preferences.clear();
  } catch (e) {
    console.error('[platform] clearAuthStorage (native) failed:', e);
  }
}

// Send the user back to a signed-out entry screen.
// On web that's the /login route. On native there is no HTTP server behind
// capacitor://localhost, so a hard navigation to "/login" resolves to a
// missing file and the webview fails to load. Reloading the bundle root
// boots the SPA fresh; with no session the router lands on the public entry.
export function redirectToSignedOut() {
  window.location.href = isNative ? '/' : '/login';
}

// Public web origin for links that must survive leaving the app.
export const WEB_ORIGIN = process.env.REACT_APP_WEB_ORIGIN || 'https://tekstak.app';

// The deployed API, used whenever a local address is meaningless.
export const PROD_API_ORIGIN = 'https://techstack-backend-fmh3.onrender.com';

/**
 * Resolve the API base for the current platform.
 *
 * The native shell can NEVER reach the dev machine's localhost — but
 * REACT_APP_API_URL is baked into the bundle at build time by CRA, and on a
 * developer's machine that variable points at http://localhost:10000. A
 * device build therefore inherited the dev backend address and every request
 * died with "Could not connect to the server".
 *
 * So on native: use the configured URL only when it is actually reachable
 * (not localhost/127.0.0.1), otherwise fall back to production.
 * On web: '' for local dev (CRA proxy handles it), configured URL otherwise.
 */
const LOCAL_ADDRESS = /^https?:\/\/(localhost|127\.0\.0\.1|0\.0\.0\.0|\[::1\])(:|\/|$)/i;

export function resolveApiBase(configuredUrl) {
  if (isNative) {
    if (configuredUrl && !LOCAL_ADDRESS.test(configuredUrl)) return configuredUrl;
    return PROD_API_ORIGIN;
  }
  if (typeof window !== 'undefined' && window.location.hostname === 'localhost') return '';
  return configuredUrl || PROD_API_ORIGIN;
}

// The local backend, for the one case that cannot ride the CRA dev proxy.
const LOCAL_BACKEND_ORIGIN = 'http://localhost:10000';

/**
 * THE API base. Every caller uses this — do not re-resolve it.
 *
 * Six modules used to work this out for themselves across two env vars
 * (REACT_APP_API_BASE and REACT_APP_API_URL) with prod and localhost:10000
 * hardcoded as fallbacks. Two of those fallbacks defeated the deliberate ''
 * below and were the reason iOS builds shipped pointing at a developer's
 * laptop. One resolver, one answer.
 *
 * REACT_APP_API_BASE wins; REACT_APP_API_URL is the older spelling, kept so
 * existing Vercel/Render settings keep working.
 *
 * Note '' on local web dev is correct, not missing: relative paths hit
 * setupProxy.js. (PRODUCTION-READINESS-ROADMAP §5 Step 2.)
 */
export const API_BASE = resolveApiBase(
  process.env.REACT_APP_API_BASE || process.env.REACT_APP_API_URL
);

/**
 * Origin for the Socket.IO connection.
 *
 * Not simply API_BASE: the CRA dev proxy forwards only /api, so the
 * /scraper-logs namespace needs an absolute origin even in local dev where
 * API_BASE is deliberately ''. Native still resolves to the deployed
 * backend, never a laptop.
 */
export const SOCKET_ORIGIN = (API_BASE || LOCAL_BACKEND_ORIGIN).replace(/\/api\/?$/, '');

// Base URL to hand to Supabase for emailed redirects (reset, verify, invite).
export function authRedirectOrigin() {
  return isNative ? WEB_ORIGIN : window.location.origin;
}

// ---------------------------------------------------------------------------
// Third-party sign-in (Google / Apple) on the native shell.
//
// The web flow is a plain top-level redirect. Native can't do that: the
// provider will not redirect back to capacitor://localhost, and Google
// explicitly refuses to render its consent screen inside an embedded
// WKWebView (`disallowed_useragent`). So native uses the OS-standard shape:
//
//   1. Ask Supabase for the authorize URL (skipBrowserRedirect: true)
//   2. Open it in SFSafariViewController via @capacitor/browser — a real
//      Safari process, which Google and Apple both accept
//   3. The provider redirects to our custom URL scheme, which wakes the app
//      through @capacitor/app's `appUrlOpen`
//   4. Exchange the returned `code` for a session, close the browser
//
// The scheme below MUST match CFBundleURLTypes in ios/App/App/Info.plist AND
// be listed under Supabase → Authentication → URL Configuration → Redirect URLs.
// ---------------------------------------------------------------------------
export const NATIVE_URL_SCHEME = 'app.tekstak.ios';
export const NATIVE_AUTH_REDIRECT = `${NATIVE_URL_SCHEME}://auth/callback`;

/**
 * Open an OAuth authorize URL and resolve with the full callback URL the
 * provider redirects back to. Rejects if the user dismisses the browser
 * without completing sign-in, so the caller can drop its loading state.
 */
export async function openOAuthAndAwaitCallback(authorizeUrl) {
  if (!isNative) throw new Error('openOAuthAndAwaitCallback is native-only');

  const [{ Browser }, { App }] = await Promise.all([
    import('@capacitor/browser'),
    import('@capacitor/app'),
  ]);

  let urlListener;
  let closeListener;
  const cleanup = () => {
    urlListener?.remove();
    closeListener?.remove();
  };

  try {
    const callbackUrl = await new Promise((resolve, reject) => {
      let settled = false;

      const settle = (fn, value) => {
        if (settled) return;
        settled = true;
        fn(value);
      };

      App.addListener('appUrlOpen', ({ url }) => {
        if (!url || !url.startsWith(`${NATIVE_URL_SCHEME}://`)) return;
        settle(resolve, url);
      }).then((h) => {
        urlListener = h;
      });

      // browserFinished fires when the user swipes/taps Done. If that happens
      // before a redirect arrived, the sign-in was abandoned — without this
      // the promise (and the spinner) would hang forever.
      Browser.addListener('browserFinished', () => {
        // The redirect and the dismissal race on completion; give appUrlOpen
        // a beat to win before calling it a cancellation.
        setTimeout(() => settle(reject, new Error('Sign-in cancelled')), 400);
      }).then((h) => {
        closeListener = h;
      });

      Browser.open({ url: authorizeUrl, presentationStyle: 'popover' }).catch((e) =>
        settle(reject, e)
      );
    });

    await Browser.close().catch(() => {});
    return callbackUrl;
  } finally {
    cleanup();
  }
}

// Light impact haptic for key actions (save, complete, capture). No-op on web.
export async function tapHaptic() {
  if (!isNative) return;
  try {
    const { Haptics, ImpactStyle } = await import('@capacitor/haptics');
    await Haptics.impact({ style: ImpactStyle.Light });
  } catch (e) {
    // Plugin absent (A3) — no-op.
  }
}
